import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/* ── helpers ──────────────────────────────────────────────────────────────── */

function fmtDuration(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtPct(rate) {
  return rate == null ? '—' : `${Math.round(rate * 100)}%`;
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '14px 16px', flex: '1 1 130px', minWidth: 130,
    }}>
      <div style={{ fontSize: 11, color: 'var(--txt-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || 'var(--txt)', marginTop: 4, lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--txt-dim)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* Stacked status bar: completed (green) + abandoned (red) + other (neutral:
   left or still waiting) = joined total, with a timing figure as a trailing
   label. Used for both by-competency and by-course.

   `metric` picks which figure that is:
     "wait"     — average time from joining to being called (the default)
     "duration" — average time in the chair, called -> completed

   Competency uses "duration". A student's wait is set by who is ahead of them,
   not by what they signed up for, so grouping wait by competency mostly
   measures when those students happened to join. Duration is a real property
   of the competency, and it is what drives everyone else's wait. */
function StackedBars({ rows, labelKey, metric = 'wait' }) {
  if (!rows || rows.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--txt-faint)', padding: '8px 0' }}>Nothing matches this filter yet.</div>;
  }
  const max = Math.max(...rows.map(r => r.count || 0), 1);
  const seg = (n, color) => n > 0 && (
    <div style={{ width: `${(n / max) * 100}%`, background: color, height: '100%' }} title={`${n}`} />
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((r, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
            <span style={{ color: 'var(--txt)' }}>{r[labelKey]}</span>
            <span style={{ color: 'var(--txt-dim)' }}>
              {r.count} joined
              {/* Null, not zero, until something has actually been completed —
                  so a competency nobody has finished yet shows no figure rather
                  than an implied "0 minutes". */}
              {metric === 'duration'
                ? r.avg_help_sec != null && ` · avg interview ${fmtDuration(r.avg_help_sec)}`
                : r.avg_wait_sec != null && ` · avg wait ${fmtDuration(r.avg_wait_sec)}`}
            </span>
          </div>
          <div style={{ display: 'flex', height: 12, background: 'var(--bg)', borderRadius: 4, overflow: 'hidden' }}>
            {seg(r.completed, 'var(--green)')}
            {seg(r.abandoned, 'var(--red, #e5484d)')}
            {seg(r.other, 'var(--txt-faint)')}
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--txt-faint)', marginTop: 3 }}>
            <span>✓ {r.completed} completed</span>
            {r.abandoned > 0 && <span>⊘ {r.abandoned} abandoned</span>}
            {r.other > 0 && <span>· {r.other} left/waiting</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Sortable per-session list. Each row: date, auto/manual badge, policy, a
   stacked mini-bar (completed/abandoned/other), joined count, completion %,
   avg wait. Sortable by date / joined / completion so "which sessions had the
   most attendance" is one click. */
function SessionList({ rows }) {
  const [sortBy, setSortBy] = useState('date');
  const [dir, setDir] = useState('desc');

  if (!rows || rows.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--txt-faint)', padding: '8px 0' }}>No sessions match this filter yet.</div>;
  }

  const sorted = [...rows].sort((a, b) => {
    let av, bv;
    if (sortBy === 'date') { av = a.date; bv = b.date; }
    else if (sortBy === 'time') { av = a.start_time || ''; bv = b.start_time || ''; }
    else if (sortBy === 'joined') { av = a.count; bv = b.count; }
    else if (sortBy === 'wait') { av = a.avg_wait_sec ?? -1; bv = b.avg_wait_sec ?? -1; }
    else { av = a.completion_rate ?? -1; bv = b.completion_rate ?? -1; }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });

  const max = Math.max(...rows.map(r => r.count || 0), 1);
  const header = (key, label, align = 'left') => (
    <th
      onClick={() => { if (sortBy === key) setDir(dir === 'asc' ? 'desc' : 'asc'); else { setSortBy(key); setDir('desc'); } }}
      style={{
        textAlign: align, padding: '6px 8px', fontSize: 11, fontWeight: 600,
        color: sortBy === key ? 'var(--amber)' : 'var(--txt-dim)', cursor: 'pointer',
        borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', userSelect: 'none',
      }}
    >
      {label}{sortBy === key ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const seg = (n, color) => n > 0 && (
    <div style={{ width: `${(n / max) * 100}%`, background: color, height: '100%' }} title={`${n}`} />
  );

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr>
          {header('date', 'Date')}
          {header('time', 'Time')}
          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, color: 'var(--txt-dim)', borderBottom: '1px solid var(--border)' }}>Type</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, color: 'var(--txt-dim)', borderBottom: '1px solid var(--border)' }}>Courses</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, color: 'var(--txt-dim)', borderBottom: '1px solid var(--border)', width: '22%' }}>Outcome</th>
          {header('joined', 'Joined', 'right')}
          {header('completion', 'Completed', 'right')}
          {header('wait', 'Avg wait', 'right')}
        </tr>
      </thead>
      <tbody>
        {sorted.map(s => (
          <tr key={s.session_id} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '7px 8px', color: 'var(--txt)', whiteSpace: 'nowrap' }}>
              {s.date}
              {s.display_label && (
                <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'monospace' }}>
                  {s.display_label}
                </div>
              )}
            </td>
            <td style={{ padding: '7px 8px', color: 'var(--txt-dim)', whiteSpace: 'nowrap' }}>{s.start_time || '—'}</td>
            <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>
              <span title={s.opened === 'auto' ? 'Auto-opened from calendar' : 'Opened manually'}
                style={{ fontSize: 11, color: 'var(--txt-dim)' }}>
                {s.opened === 'auto' ? '🕒 Auto' : '● Manual'}
              </span>
            </td>
            <td style={{ padding: '7px 8px', color: 'var(--txt-dim)', fontSize: 11 }}>
              {s.courses && s.courses.length
                ? s.courses.map(c => `${c.course_code} (${c.count})`).join(', ')
                : '—'}
            </td>
            <td style={{ padding: '7px 8px' }}>
              <div style={{ display: 'flex', height: 10, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
                {seg(s.completed, 'var(--green)')}
                {seg(s.abandoned, 'var(--red, #e5484d)')}
                {seg(s.other, 'var(--txt-faint)')}
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 9, color: 'var(--txt-faint)', marginTop: 2 }}>
                <span style={{ color: 'var(--green)' }}>{s.completed} done</span>
                {s.abandoned > 0 && <span style={{ color: 'var(--red, #e5484d)' }}>{s.abandoned} aband</span>}
                {s.other > 0 && <span>{s.other} left/wait</span>}
              </div>
            </td>
            <td style={{ padding: '7px 8px', textAlign: 'right', color: 'var(--txt)', fontWeight: 600 }}>{s.count}</td>
            <td style={{ padding: '7px 8px', textAlign: 'right', color: 'var(--txt-dim)' }}>
              {s.completed}{s.completion_rate != null && ` (${Math.round(s.completion_rate * 100)}%)`}
            </td>
            <td style={{ padding: '7px 8px', textAlign: 'right', color: 'var(--txt-dim)' }}>{fmtDuration(s.avg_wait_sec)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* Horizontal box plot for wait-time distribution: min–Q1 whisker, Q1–Q3 box,
   median line, Q3–max whisker, with a std-dev label. Scaled to a shared max. */
function BoxPlot({ label, stats, scaleMax }) {
  if (!stats || stats.n === 0) return null;
  const pos = (v) => `${(v / scaleMax) * 100}%`;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--txt)' }}>{label}</span>
        <span style={{ color: 'var(--txt-dim)', fontSize: 11 }}>
          median {fmtDuration(stats.median)} · σ {fmtDuration(stats.stddev)} · n={stats.n}
        </span>
      </div>
      <div style={{ position: 'relative', height: 22 }}>
        {/* whisker line min→max */}
        <div style={{
          position: 'absolute', top: 10, left: pos(stats.min), width: `calc(${pos(stats.max)} - ${pos(stats.min)})`,
          borderTop: '1px solid var(--txt-faint)',
        }} />
        {/* min cap */}
        <div style={{ position: 'absolute', top: 5, left: pos(stats.min), height: 10, borderLeft: '1px solid var(--txt-faint)' }} />
        {/* max cap */}
        <div style={{ position: 'absolute', top: 5, left: pos(stats.max), height: 10, borderLeft: '1px solid var(--txt-faint)' }} />
        {/* Q1→Q3 box */}
        <div style={{
          position: 'absolute', top: 3, left: pos(stats.q1),
          width: `calc(${pos(stats.q3)} - ${pos(stats.q1)})`, height: 16,
          background: 'rgba(96,165,250,0.25)', border: '1px solid var(--blue, #3b82f6)', borderRadius: 3,
        }} />
        {/* median line */}
        <div style={{ position: 'absolute', top: 3, left: pos(stats.median), height: 16, borderLeft: '2px solid var(--amber)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--txt-faint)', marginTop: 1 }}>
        <span>{fmtDuration(stats.min)}</span>
        <span>{fmtDuration(stats.max)}</span>
      </div>
    </div>
  );
}

/* ── main ─────────────────────────────────────────────────────────────────── */

export default function AnalyticsTab() {
  const { apiFetch } = useAuth();

  const [live, setLive]       = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter options + current selections.
  const [options, setOptions] = useState({ semesters: [], courses: [], competencies: [] });
  const [days, setDays]                 = useState(30);   // null = all time
  const [semesterId, setSemesterId]     = useState('');
  const [courseId, setCourseId]         = useState('');
  const [competencyName, setCompetencyName] = useState('');
  const [excludeQuestions, setExcludeQuestions] = useState(false);

  const loadLive = useCallback(async () => {
    try {
      const res = await apiFetch('/api/analytics/live');
      if (res.ok) setLive(await res.json());
    } catch { /* leave prior */ }
  }, [apiFetch]);

  const loadOptions = useCallback(async () => {
    try {
      const res = await apiFetch('/api/analytics/filters');
      if (res.ok) setOptions(await res.json());
    } catch { /* leave empty */ }
  }, [apiFetch]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (days != null) p.set('days', String(days));
      if (semesterId) p.set('semester_id', semesterId);
      if (courseId) p.set('course_id', courseId);
      if (competencyName) p.set('competency_name', competencyName);
      if (excludeQuestions) p.set('exclude_questions', 'true');
      const res = await apiFetch(`/api/analytics/summary?${p.toString()}`);
      if (res.ok) setSummary(await res.json());
    } catch { /* leave prior */ }
    finally { setLoading(false); }
  }, [apiFetch, days, semesterId, courseId, competencyName, excludeQuestions]);

  useEffect(() => { loadLive(); loadOptions(); }, []); // eslint-disable-line
  useEffect(() => { loadSummary(); }, [loadSummary]); // re-runs whenever a filter changes

  useEffect(() => {
    const id = setInterval(loadLive, 20000);
    return () => clearInterval(id);
  }, [loadLive]);

  // Cascade: when a semester is picked, the course dropdown shows only that
  // semester's courses. Competency is course-independent by design (one
  // "Welcome Interview" spans every course that teaches it), so it isn't
  // narrowed — the course filter and competency filter simply compose as AND.
  const coursesForPicker = semesterId
    ? options.courses.filter(c => String(c.semester_id) === String(semesterId))
    : options.courses;

  // If a semester change orphans the current course selection, clear it.
  useEffect(() => {
    if (courseId && !coursesForPicker.some(c => String(c.id) === String(courseId))) {
      setCourseId('');
    }
  }, [semesterId]); // eslint-disable-line

  const o = summary?.overview;

  const selectStyle = { fontSize: 12, padding: '5px 8px', minWidth: 130 };

  return (
    <div className={styles.panel}>
      {/* ── Live ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Right now</h3>
        {!live ? (
          <div style={{ fontSize: 13, color: 'var(--txt-faint)' }}>Loading…</div>
        ) : !live.active ? (
          <div style={{ fontSize: 13, color: 'var(--txt-dim)' }}>
            No active session. Open a queue to see live numbers here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <Stat label="Waiting" value={live.waiting_now} accent="var(--amber)" />
            <Stat label="In interview" value={live.being_helped_now} />
            <Stat label="Completed" value={live.completed_so_far} accent="var(--green)" />
            <Stat label="Longest wait" value={fmtDuration(live.longest_wait_sec)} />
            <Stat label="Avg wait" value={fmtDuration(live.avg_wait_sec)} />
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Historical ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 12 }}>Trends</h3>

        {/* Filter bar */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
          marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--border)',
        }}>
          <select style={selectStyle} value={semesterId} onChange={e => setSemesterId(e.target.value)}>
            <option value="">All semesters</option>
            {options.semesters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select style={selectStyle} value={courseId}
            onChange={e => setCourseId(e.target.value)}>
            <option value="">All courses</option>
            {coursesForPicker.map(c => (
              <option key={c.id} value={c.id}>{c.course_code} — {c.course_name}</option>
            ))}
          </select>

          <select style={selectStyle} value={competencyName} onChange={e => setCompetencyName(e.target.value)}>
            <option value="">All competencies</option>
            {options.competencies.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>

          <label style={{ fontSize: 12, color: 'var(--txt-dim)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={excludeQuestions}
              onChange={e => setExcludeQuestions(e.target.checked)}
              style={{ width: 15, height: 15 }} />
            Exclude questions
          </label>

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {[{ l: '7d', v: 7 }, { l: '30d', v: 30 }, { l: 'All time', v: null }].map(b => (
              <button
                key={b.l}
                className={days === b.v ? 'btn-primary' : 'btn-ghost'}
                onClick={() => setDays(b.v)}
                style={{ fontSize: 12, padding: '4px 10px' }}
              >
                {b.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--txt-faint)' }}>Loading…</div>
        ) : !o || o.total_joined === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--txt-dim)' }}>
            No queue activity matches this filter. Try widening the time range or clearing a filter.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
              <Stat label="Joined" value={o.total_joined} sub={`over ${o.sessions_held} session${o.sessions_held !== 1 ? 's' : ''}`} />
              <Stat label="Completed" value={o.completed} accent="var(--green)" />
              <Stat label="Abandoned" value={o.abandoned} sub="session closed on them"
                    accent={o.abandoned ? 'var(--red, #e5484d)' : 'var(--txt)'} />
              <Stat label="Completion" value={fmtPct(o.completion_rate)} />
              <Stat label="Avg wait" value={fmtDuration(o.avg_wait_sec)} />
              <Stat label="Avg interview" value={fmtDuration(o.avg_help_sec)} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt-dim)', marginBottom: 8 }}>
                  By competency
                </div>
                <StackedBars rows={summary.by_competency} labelKey="competency" metric="duration" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt-dim)', marginBottom: 8 }}>
                  By course
                </div>
                <StackedBars rows={summary.by_course} labelKey="course_code" />

                {summary.wait_distribution?.overall && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt-dim)', marginBottom: 8 }}>
                      Wait time distribution
                    </div>
                    {(() => {
                      const wd = summary.wait_distribution;
                      // Overall only. The per-competency box plots were removed
                      // for the same reason the bars now show duration: a wait
                      // is set by who is ahead of you, not by what you signed up
                      // for, so splitting waits by competency measured when
                      // those students happened to join rather than anything
                      // about the competency. Overall spread is the useful part.
                      const scaleMax = Math.max(wd.overall?.max || 0, 1);
                      return (
                        <>
                          <BoxPlot label="Overall" stats={wd.overall} scaleMax={scaleMax} />
                          <div style={{ fontSize: 9, color: 'var(--txt-faint)', marginTop: 6, lineHeight: 1.5 }}>
                            Box = Q1–Q3 (middle 50%), gold line = median, whiskers = min/max.
                            σ is the standard deviation. Wait = time from joining to being called;
                            entries that were never called are excluded.
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {summary.duration_distribution?.overall && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt-dim)', marginBottom: 8 }}>
                      Interview duration by competency
                    </div>
                    {(() => {
                      const dd = summary.duration_distribution;
                      // Shared scale across overall + per-competency so the
                      // boxes are comparable at a glance.
                      const scaleMax = Math.max(
                        dd.overall?.max || 0,
                        ...(dd.by_competency || []).map(c => c.max || 0),
                        1,
                      );
                      return (
                        <>
                          <BoxPlot label="Overall" stats={dd.overall} scaleMax={scaleMax} />
                          {(dd.by_competency || []).map(c => (
                            <BoxPlot key={c.competency} label={c.competency} stats={c} scaleMax={scaleMax} />
                          ))}
                          <div style={{ fontSize: 9, color: 'var(--txt-faint)', marginTop: 6, lineHeight: 1.5 }}>
                            Box = Q1–Q3 (middle 50%), gold line = median, whiskers = min/max.
                            σ is the standard deviation. Duration = time from being called to
                            being completed; entries that were never completed are excluded.
                            A wide box means that competency varies a lot between students.
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt-dim)' }}>
                  Sessions
                </div>
                <div style={{ fontSize: 10, color: 'var(--txt-faint)' }}>
                  🕒 auto · ● manual · click a column to sort
                </div>
              </div>
              <SessionList rows={summary.by_session} />
            </div>
          </>
        )}
      </section>
    </div>
  );
}
